import React from 'react';

import { StudyStatusEnum } from '@shared/types/status';
import { StatusPill } from '@shared/ui/StatusPill/StatusPill';
import { d7Format } from '@shared/utils/date';

import { twMerge } from '@utils/twMerge';
import { STATUS_PILL_WIDTH } from 'src/pages/ClinicalTrial/SupportSubjectList/QCTasks/constants';

export const renderDateTime = (
  v?: string,
  dateTextClass?: string,
  timeTextClass?: string
) => {
  if (!v) return '—';

  const datePart = v.substring(0, 10);
  const timePart = v.substring(11, 19);

  // datePart is the date-only portion (YYYY-MM-DD); render it as-is so the
  // calendar date is not shifted by the local timezone (D7S-528).
  const formattedDate = d7Format(datePart, 'dd-MMM-yyyy');

  return (
    <div className='leading-tight'>
      <div className={twMerge('font-medium', dateTextClass)}>
        {formattedDate}
      </div>

      {timeTextClass && (
        <div className={twMerge('text-[12px]', timeTextClass)}>{timePart}</div>
      )}
    </div>
  );
};

export const renderNameEmail = (
  name?: string,
  email?: string,
  nameClass?: string,
  emailClass?: string
) => {
  if (!name && !email) return '—';
  return (
    <div className='leading-tight min-w-0'>
      <div className={twMerge('font-medium truncate', nameClass)} title={name}>
        {name ?? '—'}
      </div>
      <div
        className={twMerge('text-[12px] truncate', emailClass)}
        title={email}
      >
        {email ?? '—'}
      </div>
    </div>
  );
};

export const toStatusEnum = (status?: string): StudyStatusEnum | undefined => {
  if (!status) return undefined;
  const statusEnum = (
    Object.keys(StudyStatusEnum) as Array<keyof typeof StudyStatusEnum>
  ).find(key => key.toLowerCase() === status.toLowerCase());
  return statusEnum
    ? StudyStatusEnum[statusEnum]
    : (status as unknown as StudyStatusEnum);
};

export const renderStatus = (status?: string) => {
  if (!status) {
    return '—';
  }

  const statusEnum = toStatusEnum(status);
  if (!statusEnum) {
    return '—';
  }

  return (
    <div
      style={{ width: STATUS_PILL_WIDTH }}
      className='inline-flex items-center'
    >
      <StatusPill
        className='font-semibold text-[14px] whitespace-nowrap overflow-hidden text-ellipsis'
        status={statusEnum}
      />
    </div>
  );
};

export const formatVisitDateLabel = (date?: string): string | undefined => {
  if (!date) return '';
  return d7Format(date, 'dd-MMM-yyyy');
};

export const formatDate = (
  date?: string,
  dateFormat: string = 'MMM-yyyy'
): string | undefined => {
  if (!date) return '';

  return d7Format(date, dateFormat);
};

export const filterByVisitId = <T extends { visitId?: string | null }>(
  items: T[],
  visitId?: string | null
): T[] => {
  if (visitId === undefined || visitId === '') return items;

  return items.filter(item => (item.visitId ?? null) === visitId);
};
